import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { GET_CATEGORIES_FOR_CTA_PICKER } from '@/containers/Categories/categoriesQueries'
import { GET_ALL_PAGES_FOR_PICKER } from '@/containers/Pages/pageQueries'
import { GET_COLLECTIONS_FOR_CTA_PICKER } from '@/containers/Collections/collectionsQueries'

export type CtaTargetType = 'cms_page' | 'category' | 'store' | 'collection'

export interface CtaTargetValue {
  type: CtaTargetType | null
  id: string | number | null
}

interface CtaTargetSelectProps {
  value: CtaTargetValue
  onChange: (next: { type: CtaTargetType; id: string | number | null }) => void
  /**
   * A page id to exclude from the CMS Pages group (e.g. the page being
   * edited), along with all of its descendants.
   */
  pageIdToExclude?: string
  /** Active language code, used to resolve category/page labels (falls back to each item's first profile). */
  language: string
  /**
   * Also fetch + render a "Collections" group (flat list, no tree). Opt-in
   * and defaults to false so existing callers keep their current query
   * shape and result set unchanged.
   */
  includeCollections?: boolean
}

interface CategoryProfile {
  language: string
  name: string
  meta_information?: { permalink?: string | null } | null
}

interface CategoryPickerNode {
  id: number
  parent_categoryId: number | null
  category_profiles: CategoryProfile[]
}

interface PagePickerNode {
  id: string
  parentId: string | null
  arrangement: number
  page_profiles: { language: string; title?: string; slug?: string }[]
}

// Titles occasionally contain a literal "\n" — either a real newline or the
// two-character backslash-n sequence typed into the CMS field — which
// otherwise renders inline in the picker. Collapse both to a single space.
const sanitizeTitle = (title: string): string =>
  title.replace(/\\n|\n/g, ' ').replace(/\s+/g, ' ').trim()

interface CollectionProfile {
  language: string
  name: string
}

interface CollectionPickerNode {
  id: number
  collection_profiles: CollectionProfile[]
}

function getCategoryLabel(profiles: CategoryProfile[], language: string): string {
  return (
    profiles.find(p => p.language === language)?.name ||
    profiles[0]?.name ||
    'Unknown'
  )
}

function getCollectionLabel(profiles: CollectionProfile[], language: string): string {
  return (
    profiles.find(p => p.language === language)?.name ||
    profiles[0]?.name ||
    'Unknown'
  )
}

function getPageLabel(
  profiles: { language: string; title?: string }[],
  language: string
): string {
  const raw =
    profiles.find(p => p.language === language)?.title ||
    profiles[0]?.title ||
    ''
  return sanitizeTitle(raw)
}

// Flattens the parent/child category tree (by `parent_categoryId`) into
// depth-first order with a per-item indentation depth — mirrors
// `PageParentSelect`'s page-tree traversal.
function flattenCategoryTree(
  categories: CategoryPickerNode[]
): { category: CategoryPickerNode; depth: number }[] {
  const childrenByParent = new Map<number | null, CategoryPickerNode[]>()
  for (const category of categories) {
    const key = category.parent_categoryId ?? null
    if (!childrenByParent.has(key)) childrenByParent.set(key, [])
    childrenByParent.get(key)!.push(category)
  }

  const flattened: { category: CategoryPickerNode; depth: number }[] = []
  const visit = (
    parentId: number | null,
    depth: number,
    visited: Set<number>
  ) => {
    const children = childrenByParent.get(parentId) ?? []
    for (const child of children) {
      if (visited.has(child.id)) continue
      visited.add(child.id)
      flattened.push({ category: child, depth })
      visit(child.id, depth + 1, visited)
    }
  }
  visit(null, 0, new Set())
  return flattened
}

/**
 * One grouped internal-target picker for the CTA editor. Groups, in order:
 * Store (the "All Products" root), Categories (hierarchical, indented), and
 * CMS Pages (existing tree + self/descendant exclusion). Reuses the same
 * Popover/Command combobox pattern as `PageParentSelect`.
 */
export default function CtaTargetSelect({
  value,
  onChange,
  pageIdToExclude,
  language,
  includeCollections = false,
}: CtaTargetSelectProps) {
  const [open, setOpen] = useState(false)

  const { data: categoriesData, loading: loadingCategories } = useQuery(
    GET_CATEGORIES_FOR_CTA_PICKER,
    { fetchPolicy: 'cache-and-network' }
  )
  const { data: pagesData, loading: loadingPages } = useQuery(
    GET_ALL_PAGES_FOR_PICKER,
    { fetchPolicy: 'cache-and-network' }
  )
  const { data: collectionsData, loading: loadingCollections } = useQuery(
    GET_COLLECTIONS_FOR_CTA_PICKER,
    { fetchPolicy: 'cache-and-network', skip: !includeCollections }
  )

  const categories: CategoryPickerNode[] = categoriesData?.findManyCategory ?? []
  const pages: PagePickerNode[] = pagesData?.findManyPage ?? []
  const collections: CollectionPickerNode[] = includeCollections
    ? (collectionsData?.findManyCollection ?? [])
    : []

  const categoryTree = useMemo(() => flattenCategoryTree(categories), [categories])

  // Page tree + self/descendant exclusion — same approach as `PageParentSelect`.
  const childrenByParent = useMemo(() => {
    const childrenByParent = new Map<string | null, PagePickerNode[]>()
    for (const page of pages) {
      const key = page.parentId ?? null
      if (!childrenByParent.has(key)) childrenByParent.set(key, [])
      childrenByParent.get(key)!.push(page)
    }
    return childrenByParent
  }, [pages])

  const collectDescendants = (startId: string): Set<string> => {
    const result = new Set<string>()
    const queue = [startId]
    while (queue.length > 0) {
      const current = queue.shift()!
      result.add(current)
      const children = childrenByParent.get(current) ?? []
      for (const child of children) {
        if (!result.has(child.id)) queue.push(child.id)
      }
    }
    return result
  }

  const excludedIds: Set<string> = useMemo(() => {
    if (!pageIdToExclude) return new Set()
    return collectDescendants(pageIdToExclude)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIdToExclude, childrenByParent])

  // Flatten the parent/child page tree (by `parentId`) into depth-first
  // order with a per-item indentation depth — same approach as
  // `flattenCategoryTree` above (and `PageParentSelect`/`PagesTree`).
  // Rendering in raw fetch order (as before) has no relationship to the
  // parent/child hierarchy, so a child could be listed above its parent
  // while still being indented as if nested under it.
  const pageTree = useMemo(() => {
    const availablePages = pages.filter(p => !excludedIds.has(p.id))
    const availableIds = new Set(availablePages.map(p => p.id))
    const childrenByAvailableParent = new Map<string | null, PagePickerNode[]>()
    for (const page of availablePages) {
      const key = page.parentId && availableIds.has(page.parentId) ? page.parentId : null
      if (!childrenByAvailableParent.has(key)) childrenByAvailableParent.set(key, [])
      childrenByAvailableParent.get(key)!.push(page)
    }

    const sortSiblings = (nodes: PagePickerNode[]) =>
      [...nodes].sort((a, b) => {
        if (a.arrangement !== b.arrangement) return a.arrangement - b.arrangement
        return getPageLabel(a.page_profiles, language).localeCompare(
          getPageLabel(b.page_profiles, language)
        )
      })

    const flattened: { page: PagePickerNode; depth: number }[] = []
    const visit = (parentId: string | null, depth: number, visited: Set<string>) => {
      const children = sortSiblings(childrenByAvailableParent.get(parentId) ?? [])
      for (const child of children) {
        if (visited.has(child.id)) continue
        visited.add(child.id)
        flattened.push({ page: child, depth })
        visit(child.id, depth + 1, visited)
      }
    }
    visit(null, 0, new Set())
    return flattened
  }, [pages, excludedIds, language])

  const selectedLabel = useMemo(() => {
    if (value.type === 'store') return 'All Products (Store)'
    if (value.type === 'category') {
      const category = categories.find(c => c.id === value.id)
      return category
        ? getCategoryLabel(category.category_profiles, language)
        : `Category #${value.id}`
    }
    if (value.type === 'cms_page') {
      const page = pages.find(p => p.id === value.id)
      return page
        ? getPageLabel(page.page_profiles, language) || page.id
        : String(value.id ?? '')
    }
    if (value.type === 'collection') {
      const collection = collections.find(c => c.id === value.id)
      return collection
        ? getCollectionLabel(collection.collection_profiles, language)
        : `Collection #${value.id}`
    }
    return 'Select a target'
  }, [value, categories, pages, collections, language])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search targets..." />
          <CommandList>
            <CommandEmpty>
              {loadingCategories || loadingPages || loadingCollections
                ? 'Loading...'
                : 'No results found.'}
            </CommandEmpty>
            <CommandGroup heading="Store">
              <CommandItem
                value="store-all-products"
                onSelect={() => {
                  onChange({ type: 'store', id: null })
                  setOpen(false)
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    value.type === 'store' ? 'opacity-100' : 'opacity-0'
                  )}
                />
                All Products (Store)
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Categories">
              {categoryTree.map(({ category, depth }) => {
                const label = getCategoryLabel(category.category_profiles, language)
                return (
                  <CommandItem
                    key={`category-${category.id}`}
                    value={`category-${category.id}-${label}`}
                    onSelect={() => {
                      onChange({ type: 'category', id: category.id })
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value.type === 'category' && value.id === category.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <span style={{ paddingLeft: depth * 16 }} className="truncate">
                      {label}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            <CommandGroup heading="CMS Pages">
              {pageTree.map(({ page, depth }) => {
                const label = getPageLabel(page.page_profiles, language)
                return (
                  <CommandItem
                    key={`page-${page.id}`}
                    value={`page-${page.id}-${label}`}
                    onSelect={() => {
                      onChange({ type: 'cms_page', id: page.id })
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value.type === 'cms_page' && value.id === page.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <span style={{ paddingLeft: depth * 16 }} className="truncate">
                      {label || page.id}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {includeCollections && (
              <CommandGroup heading="Collections">
                {collections.map(collection => {
                  const label = getCollectionLabel(collection.collection_profiles, language)
                  return (
                    <CommandItem
                      key={`collection-${collection.id}`}
                      value={`collection-${collection.id}-${label}`}
                      onSelect={() => {
                        onChange({ type: 'collection', id: collection.id })
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          value.type === 'collection' && value.id === collection.id
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <span className="truncate">{label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
